import {
  BottomSheetView,
  type BottomSheetBackgroundProps,
} from "@gorhom/bottom-sheet";
import { forwardRef, ReactNode, useMemo } from "react";
import { StyleSheet, View } from "react-native";
import {
  type SheetAdapterRef,
  useBottomSheetContext,
} from "react-native-bottom-sheet-stack";
import { GorhomSheetAdapter } from "react-native-bottom-sheet-stack/gorhom";
import type { GorhomSheetAdapterProps } from "react-native-bottom-sheet-stack/gorhom";

const WEB_MODAL_CORNER_RADIUS = 28;

const WebModalHandle = () => (
  <View style={styles.handleContainer} pointerEvents="none">
    <View style={styles.handle} />
  </View>
);

const WebModalBackground = ({
  pointerEvents,
  style,
  surface,
}: BottomSheetBackgroundProps & { surface: ReactNode }) => (
  <View pointerEvents={pointerEvents} style={[style, styles.surfaceContainer]}>
    {surface}
  </View>
);

type AppModalSheetProps = {
  children: ReactNode | ((context: { closeModal: () => void }) => ReactNode);
  dismissKeyboardOnClose?: boolean;
  keyboardBehavior?: GorhomSheetAdapterProps["keyboardBehavior"];
  showHandle?: boolean;
  surface?: ReactNode;
};

const AppModalSheet = forwardRef<SheetAdapterRef, AppModalSheetProps>(
  function AppModalSheet(
    { children, keyboardBehavior = "interactive", showHandle = false, surface },
    ref,
  ) {
    const { close } = useBottomSheetContext();
    const content =
      typeof children === "function"
        ? children({ closeModal: close })
        : children;
    const backgroundComponent = useMemo(() => {
      if (!surface) return undefined;

      return function AppModalBackground(props: BottomSheetBackgroundProps) {
        return <WebModalBackground {...props} surface={surface} />;
      };
    }, [surface]);

    return (
      <GorhomSheetAdapter
        ref={ref}
        backgroundComponent={backgroundComponent}
        backgroundStyle={surface ? styles.customSurface : styles.sheetSurface}
        enableDynamicSizing
        handleComponent={showHandle ? WebModalHandle : null}
        keyboardBehavior={keyboardBehavior}
      >
        <BottomSheetView>
          <View style={styles.content}>{content}</View>
        </BottomSheetView>
      </GorhomSheetAdapter>
    );
  },
);

export default AppModalSheet;

const styles = StyleSheet.create({
  content: {
    overflow: "hidden",
  },
  handle: {
    backgroundColor: "rgba(17, 17, 23, 0.34)",
    borderRadius: 999,
    height: 4,
    width: 48,
  },
  handleContainer: {
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.1)",
    height: 20,
    paddingTop: 4,
  },
  sheetSurface: {
    backgroundColor: "rgba(245, 247, 252, 0.96)",
    borderTopLeftRadius: WEB_MODAL_CORNER_RADIUS,
    borderTopRightRadius: WEB_MODAL_CORNER_RADIUS,
  },
  customSurface: {
    backgroundColor: "transparent",
    borderTopLeftRadius: WEB_MODAL_CORNER_RADIUS,
    borderTopRightRadius: WEB_MODAL_CORNER_RADIUS,
    overflow: "hidden",
  },
  surfaceContainer: {
    overflow: "hidden",
  },
});
